import { useCallback, useEffect, useRef, useState } from "react";
import type { PointerEvent as ReactPointerEvent } from "react";

/**
 * Makes a floating action button draggable across the viewport.
 *
 * Why: a fixed-position button can sit on top of content the user is trying to
 * read/tap (e.g. the bottom-left cards in a list). Letting the user drag it out
 * of the way — and remembering where they put it — solves that without hiding
 * the control.
 *
 * Behaviour:
 * - Works with mouse and touch via Pointer Events.
 * - A press that moves less than {@link DRAG_THRESHOLD} counts as a click, so
 *   the button still navigates on a normal tap.
 * - On release the button snaps to the nearest side (left/right) so it stays
 *   docked at an edge and clear of the content in the middle.
 * - The chosen spot is saved to localStorage and re-clamped to the current
 *   viewport on load and on resize/orientation changes.
 */

const STORAGE_KEY = "backButtonPosition";
// Must match the rendered button size in DraggableBackButton.module.scss.
const BUTTON_SIZE = 32;
const EDGE_MARGIN = 16;
const DEFAULT_BOTTOM_OFFSET = 20;
// Movement (px) allowed before a press is treated as a drag rather than a tap.
const DRAG_THRESHOLD = 6;

export interface Position {
  x: number;
  y: number;
}

const readSafeAreaBottom = (): number => {
  if (typeof document === "undefined") return 0;
  const probe = document.createElement("div");
  probe.style.cssText =
    "position:fixed;bottom:0;height:env(safe-area-inset-bottom,0px);visibility:hidden;pointer-events:none;";
  document.body.appendChild(probe);
  const value = probe.getBoundingClientRect().height;
  probe.remove();
  return value;
};

const clampToViewport = (pos: Position, safeBottom: number): Position => {
  const maxX = window.innerWidth - BUTTON_SIZE - EDGE_MARGIN;
  const maxY = window.innerHeight - BUTTON_SIZE - EDGE_MARGIN - safeBottom;
  return {
    x: Math.min(Math.max(pos.x, EDGE_MARGIN), Math.max(EDGE_MARGIN, maxX)),
    y: Math.min(Math.max(pos.y, EDGE_MARGIN), Math.max(EDGE_MARGIN, maxY)),
  };
};

const getDefaultPosition = (safeBottom: number): Position => ({
  x: EDGE_MARGIN,
  y: window.innerHeight - BUTTON_SIZE - DEFAULT_BOTTOM_OFFSET - safeBottom,
});

const readStoredPosition = (): Position | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      parsed &&
      typeof parsed.x === "number" &&
      typeof parsed.y === "number" &&
      Number.isFinite(parsed.x) &&
      Number.isFinite(parsed.y)
    ) {
      return parsed;
    }
  } catch {
    // Ignore malformed/blocked storage and fall back to the default spot.
  }
  return null;
};

const snapToNearestSide = (pos: Position): Position => {
  const center = pos.x + BUTTON_SIZE / 2;
  const snappedX =
    center < window.innerWidth / 2
      ? EDGE_MARGIN
      : window.innerWidth - BUTTON_SIZE - EDGE_MARGIN;
  return { x: snappedX, y: pos.y };
};

export const useDraggableFab = () => {
  const safeBottomRef = useRef(0);

  const [position, setPosition] = useState<Position>(() => {
    if (typeof window === "undefined") return { x: EDGE_MARGIN, y: EDGE_MARGIN };
    const safeBottom = readSafeAreaBottom();
    safeBottomRef.current = safeBottom;
    const stored = readStoredPosition();
    return clampToViewport(stored ?? getDefaultPosition(safeBottom), safeBottom);
  });

  const [isDragging, setIsDragging] = useState(false);

  // Tracks the in-progress gesture so pointer-move can compute deltas and
  // pointer-up can tell a drag from a tap.
  const gestureRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    originX: number;
    originY: number;
    moved: boolean;
  } | null>(null);
  // Set true when the just-finished gesture was a drag, so the click that the
  // browser fires afterwards is suppressed (keyboard clicks are unaffected).
  const suppressClickRef = useRef(false);

  // Keep the button on-screen when the viewport changes (resize/rotate).
  useEffect(() => {
    const onResize = () => {
      safeBottomRef.current = readSafeAreaBottom();
      setPosition((current) =>
        clampToViewport(current, safeBottomRef.current),
      );
    };
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, []);

  const onPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLButtonElement>) => {
      // Only react to the primary button / a single touch point.
      if (event.button !== 0 && event.pointerType === "mouse") return;
      // Start each gesture clean: if a previous drag's trailing click never
      // arrived (browser-dependent), don't let it swallow this tap.
      suppressClickRef.current = false;
      gestureRef.current = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        originX: position.x,
        originY: position.y,
        moved: false,
      };
      event.currentTarget.setPointerCapture(event.pointerId);
    },
    [position.x, position.y],
  );

  const onPointerMove = useCallback(
    (event: ReactPointerEvent<HTMLButtonElement>) => {
      const gesture = gestureRef.current;
      if (!gesture || gesture.pointerId !== event.pointerId) return;

      const dx = event.clientX - gesture.startX;
      const dy = event.clientY - gesture.startY;

      if (!gesture.moved && Math.hypot(dx, dy) < DRAG_THRESHOLD) return;

      gesture.moved = true;
      setIsDragging(true);
      setPosition(
        clampToViewport(
          { x: gesture.originX + dx, y: gesture.originY + dy },
          safeBottomRef.current,
        ),
      );
    },
    [],
  );

  const endGesture = useCallback(
    (event: ReactPointerEvent<HTMLButtonElement>) => {
      const gesture = gestureRef.current;
      if (!gesture || gesture.pointerId !== event.pointerId) return;
      gestureRef.current = null;

      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }

      if (!gesture.moved) {
        setIsDragging(false);
        return;
      }

      // A real drag happened: suppress the trailing click and dock to a side.
      suppressClickRef.current = true;
      setIsDragging(false);
      setPosition((current) => {
        const snapped = clampToViewport(
          snapToNearestSide(current),
          safeBottomRef.current,
        );
        try {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(snapped));
        } catch {
          // Storage may be unavailable (private mode / quota); position still
          // applies for the current session.
        }
        return snapped;
      });
    },
    [],
  );

  // Returns true if the click should be ignored because it followed a drag.
  const consumeClickAfterDrag = useCallback(() => {
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      return true;
    }
    return false;
  }, []);

  return {
    position,
    isDragging,
    dragHandlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp: endGesture,
      onPointerCancel: endGesture,
    },
    consumeClickAfterDrag,
  };
};
